import { Grid } from '@mui/material';
import { AddItemButton } from '@tassat/button';
import { ActionTypes, AdminRoleId, doesWorkflowRejectCodeExist } from '@tassat/common-definitions';
import { WithdrawalWalletMappingForm } from '@tassat/forms';
import { AccountRecord } from '@tassat/fred-definitions';
import { FormModal } from '@tassat/modal';
import {
    ConfirmationWorkflowAction,
    ConfirmationWorkflowConsumer,
    ConfirmationWorkflowContext,
    ConfirmationWorkflowReducer,
    DEFAULT_INITIAL_STATE
} from '@tassat/workflow';
import React from 'react';

import { submitWithdrawalWalletMappingForm } from 'Containers/admin/forms/helpers/withdrawalWalletMappingFormFn';
import Worker from 'Lib/worker/PrimusWorkerWrapper';
import { useAppSelector } from 'Reducers/store/hooks';
import { getAccountWithdrawalRelationshipsByFirmId, getAssociatedWalletsbyAccountWithdrawalDataId, getUnmappedWalletsByAccountWithdrawalDataId } from 'Selectors/accountsWithdrawalRelationships';
import { AssociatedWalletsGrid } from 'SharedComponents/withdrawalInstructions/withdrawalWalletGrid';

import { FirmWithdrawalWalletsProps } from './types';

export function FirmWithdrawalWallets(props: FirmWithdrawalWalletsProps) {
    const {
        classes,
        withdrawalInstruction,
        userRole,
        firm
    } = props;
    const associatedWallets = useAppSelector((state) => {
        return getAssociatedWalletsbyAccountWithdrawalDataId(
            state, withdrawalInstruction.FirmId, withdrawalInstruction.AccountWithdrawalDataId
        );
    });
    const openWallets = useAppSelector((state) => {
        return getUnmappedWalletsByAccountWithdrawalDataId(
            state, withdrawalInstruction.FirmId, withdrawalInstruction.AccountWithdrawalDataId
        );
    });
    const accountRelationships = useAppSelector((state) => {
        return getAccountWithdrawalRelationshipsByFirmId(state, withdrawalInstruction.FirmId);
    });

    const [ mapAssociatedWalletsModalOpen, setMapAssociatedWalletsModalOpen ] = React.useState<boolean>(false);
    const [ confirmationWorkflowState, dispatchConfirmationWorkflowAction ] =
    React.useReducer(ConfirmationWorkflowReducer, DEFAULT_INITIAL_STATE);
    const confirmationContext = React.useMemo(() => {
        return {
            confirmationWorkflowState,
            dispatchConfirmationWorkflowAction
        };
    }, [ confirmationWorkflowState, dispatchConfirmationWorkflowAction ]);

    const unmapWalletFromWithdrawalInstructionValidation = async (account: AccountRecord) => {
        if (account.AccessControl) {
            const pendingTransactions = {
                Condition: 'isCompleted|EQ|false:' +
                    `AccountWithdrawalDataId|EQ|${withdrawalInstruction.AccountWithdrawalDataId}:` +
                    `wallet|EQ|${account.Account}`,
                PageNo: 1,
                RecordPerPage: 4,
                Sort: 'auditTime|DESC'
            };

            try {
                const res = await Worker.getInstance()
                    .getTransactions(pendingTransactions);

                if (res.data?.TotalCount > 0) {
                    const validationFailedAction: ConfirmationWorkflowAction = {
                        type: 'VALIDATION_FAILED',
                        workflowConfiguration: {
                            validationTitle: `Could not umnap instruction ${withdrawalInstruction.Description}`,
                            validationMessage: `Withdrawal Instruction ${withdrawalInstruction.Description} ` +
                                `cannot be unmapped from account ${account.AccountName} due to pending transactions.`
                        }
                    };
                    dispatchConfirmationWorkflowAction(validationFailedAction);

                    return true;
                }
            } catch (error) {
                const validationFailedAction: ConfirmationWorkflowAction = {
                    type: 'VALIDATION_FAILED',
                    workflowConfiguration: {
                        validationTitle: `Could not validate pending transactions for instruction ${withdrawalInstruction.Description}`,
                        validationMessage: 'Could not validate if there are pending transactions, please try again or reach out to support if the issue persists.'
                    }
                };
                dispatchConfirmationWorkflowAction(validationFailedAction);

                return true;
            }
        }

        const validationSuccessfulAction: ConfirmationWorkflowAction = {
            type: 'VALIDATION_SUCCESS',
            workflowConfiguration: {
                selectedItem: account,
                actionType: ActionTypes.unmap,
                confirmTitle: `Confirm Unmapping ${withdrawalInstruction.Description}`,
                confirmBody: `Please confirm removing permissions to account ${account.AccountName} for withdrawal Instruction ${withdrawalInstruction.Description}.`
            }
        };
        dispatchConfirmationWorkflowAction(validationSuccessfulAction);

        return false;
    };

    const unmapWalletFromWithdrawalInstruction = async (account: AccountRecord) => {
        const relationshipArray = accountRelationships.filter(relationship => relationship.AccountId === account.AccountId &&
            relationship.AccountWithdrawalDataId === withdrawalInstruction.AccountWithdrawalDataId);
        try {
            const response = await Worker.getInstance()
                .deleteAccountWithdrawalRelationship({
                    AccountId: account.AccountId,
                    AccountWithdrawalRelationshipId: relationshipArray[0].AccountWithdrawalRelationshipId
                });

            if (response.data && response.data.Status === 48) {
                const serverSuccessAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_SUCCESS',
                    workflowConfiguration: {
                        successTitle: `Successfully Unmapped Withdrawal Instruction ${withdrawalInstruction.Description}`,
                        successBody: `Successfully unmapped the withdrawal instruction ${withdrawalInstruction.Description} from account ${account.AccountName}.`
                    }
                };
                dispatchConfirmationWorkflowAction(serverSuccessAction);
            } else if (response.data && doesWorkflowRejectCodeExist(response.data.Status)) {
                const serverErrorAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_ERROR',
                    workflowConfiguration: {
                        failureTitle: `Failed to Unmap Withdrawal Instruction ${withdrawalInstruction.Description}`,
                        failureBody: `Could not unmap the withdrawal instruction ${withdrawalInstruction.Description} from account ${account.AccountName}.`
                    }
                };
                dispatchConfirmationWorkflowAction(serverErrorAction);
            } else {
                throw new Error();
            }
        } catch (error) {
            const serverErrorAction: ConfirmationWorkflowAction = {
                type: 'SERVER_ERROR',
                workflowConfiguration: {
                    failureTitle: `Failed to Unmap Withdrawal Instruction ${withdrawalInstruction.Description}}`,
                    failureBody: `Something went wrong unmapping the  withdrawal instruction ${withdrawalInstruction.Description} from account ${account.AccountName}.`
                }
            };
            dispatchConfirmationWorkflowAction(serverErrorAction);
        }
    };

    React.useEffect(() => {
        //set initial confirmation settings
        const configureConfirmationWorkflowAction: ConfirmationWorkflowAction = {
            type: 'CONFIGURE_WORKFLOW',
            workflowConfiguration: {
                setSelectedItem: () => withdrawalInstruction,
                preActionValidationAndConfig: { [ActionTypes.unmap]: unmapWalletFromWithdrawalInstructionValidation },
                onConfirm: { [ActionTypes.unmap]: unmapWalletFromWithdrawalInstruction },
                useTimeout: true
            }
        };
        dispatchConfirmationWorkflowAction(configureConfirmationWorkflowAction);
    }, [accountRelationships]);

    return (
        <ConfirmationWorkflowContext.Provider value={confirmationContext}>
            <Grid
                className={classes.root}
                container
                direction='column'
                spacing={0}
                wrap='nowrap'
            >
                {userRole.AdminRoleId !== AdminRoleId.BANK_OPS_SUPERVISOR &&
                    userRole.AdminRoleId !== AdminRoleId.PCG_ADMIN &&
                    !firm?.IsClosed &&
                    (
                    <Grid
                        className={classes.mapWalletContainer}
                        item
                    >
                        <AddItemButton
                            onClick={() => setMapAssociatedWalletsModalOpen(true)}
                            text='Map Account(s)'
                        />
                    </Grid>
                )}
                <AssociatedWalletsGrid
                    associatedWallets={associatedWallets}
                    firm={firm}
                    userRole={userRole}
                />
                <FormModal
                    Form={WithdrawalWalletMappingForm}
                    formProps={{
                        withdrawalInstruction: withdrawalInstruction,
                        wallets: openWallets,
                        submitWithdrawalWalletMappingForm: submitWithdrawalWalletMappingForm,
                        associatedWallets: associatedWallets
                    }}
                    onClose={() => setMapAssociatedWalletsModalOpen(false)}
                    open={mapAssociatedWalletsModalOpen}
                    title='Map Account(s)'
                />
            </Grid>
            <ConfirmationWorkflowConsumer />
        </ConfirmationWorkflowContext.Provider>
    );
}